import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { 
  AlertTriangle, Plus, FileText, Clock, CheckCircle, XCircle, 
  User, Calendar, Pen, Send, Eye, Search
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import api from '../lib/api';

const AccidentReports = () => {
  const { user } = useAuth();
  const isAdminOrStaff = user?.role === 'admin' || user?.role === 'staff';
  const isTeacher = user?.role === 'teacher';
  const canCreate = isAdminOrStaff || isTeacher;

  const [reports, setReports] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewReport, setViewReport] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [reportForm, setReportForm] = useState({
    student_id: '',
    student_name: '',
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: new Date().toTimeString().slice(0, 5),
    location: '',
    description: '',
    injuries: '',
    treatment_given: '',
    witnesses: '',
    requires_medical_attention: false
  });

  const [signatureForm, setSignatureForm] = useState({
    signer_name: '',
    relationship: 'parent',
    approved: true,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, studentsRes] = await Promise.all([
        api.get('/accident-reports'),
        api.get('/students/list')
      ]);
      setReports(reportsRes.data || []);
      setStudents(studentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    if (!reportForm.student_name || !reportForm.description) {
      toast.error('Please fill in student name and incident description');
      return;
    }

    try {
      await api.post('/accident-reports', reportForm);
      toast.success('Accident report created and parent notified');
      setCreateModalOpen(false);
      setReportForm({
        student_id: '',
        student_name: '',
        incident_date: new Date().toISOString().split('T')[0],
        incident_time: new Date().toTimeString().slice(0, 5),
        location: '',
        description: '',
        injuries: '',
        treatment_given: '',
        witnesses: '',
        requires_medical_attention: false
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create report:', error);
      toast.error('Failed to create report');
    }
  };

  const handleSignReport = async () => {
    if (!signatureForm.signer_name) {
      toast.error('Please enter your name');
      return;
    }

    try {
      await api.post(`/accident-reports/${viewReport.id}/sign`, signatureForm);
      toast.success('Report signed successfully');
      setSignatureModalOpen(false);
      setViewReport(null);
      setSignatureForm({
        signer_name: '',
        relationship: 'parent',
        approved: true,
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Failed to sign report:', error);
      toast.error('Failed to sign report');
    }
  };

  const getStatusBadge = (report) => {
    if (report.signed_at) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Signed</Badge>;
    }
    if (report.requires_medical_attention) {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Urgent</Badge>;
    }
    return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending Signature</Badge>;
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = !searchQuery || 
      report.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'signed' && report.signed_at) ||
      (statusFilter === 'pending' && !report.signed_at);
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="accident-reports-page">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0a] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                Accident Reports
              </h1>
              <p className="text-white/60 mt-2">Document incidents and obtain parent acknowledgment</p>
            </div>
            {canCreate && (
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="bg-red-500 hover:bg-red-600 text-white font-bold"
                data-testid="create-report-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Report
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a2e] border-white/10">
              <SelectItem value="all" className="text-white">All Reports</SelectItem>
              <SelectItem value="pending" className="text-white">Pending Signature</SelectItem>
              <SelectItem value="signed" className="text-white">Signed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Reports List */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredReports.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 mx-auto text-white/20 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No accident reports</h3>
              <p className="text-white/60">
                {reports.length === 0 ? "No incidents have been recorded yet" : "No reports match your filters"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredReports.map(report => (
              <Card 
                key={report.id} 
                className={`bg-white/5 border-white/10 hover:border-white/20 transition-all cursor-pointer ${report.requires_medical_attention && !report.signed_at ? 'border-l-4 border-l-red-500' : ''}`}
                onClick={() => setViewReport(report)}
                data-testid={`report-${report.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{report.student_name}</h3>
                        {getStatusBadge(report)}
                      </div>
                      <p className="text-white/60 line-clamp-2 mb-2">{report.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-white/50">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(report.incident_date || report.date_time)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {report.incident_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {report.created_by_name}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setViewReport(report); }}
                      className="border-white/10 text-white hover:bg-white/10"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Report Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              New Accident Report
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Document the incident details. The parent will be notified via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-white/70 mb-1 block">Student Name *</label>
              <Input
                placeholder="Full name of student"
                value={reportForm.student_name}
                onChange={(e) => setReportForm({ ...reportForm, student_name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/70 mb-1 block">Date</label>
                <Input
                  type="date"
                  value={reportForm.incident_date}
                  onChange={(e) => setReportForm({ ...reportForm, incident_date: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Time</label>
                <Input
                  type="time"
                  value={reportForm.incident_time}
                  onChange={(e) => setReportForm({ ...reportForm, incident_time: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Location</label>
              <Input
                placeholder="Where did the incident occur?"
                value={reportForm.location}
                onChange={(e) => setReportForm({ ...reportForm, location: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Description of Incident *</label>
              <Textarea
                placeholder="What happened?"
                value={reportForm.description}
                onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Injuries Sustained</label>
              <Textarea
                placeholder="Describe any injuries"
                value={reportForm.injuries}
                onChange={(e) => setReportForm({ ...reportForm, injuries: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Treatment Given</label>
              <Textarea
                placeholder="First aid or treatment provided"
                value={reportForm.treatment_given}
                onChange={(e) => setReportForm({ ...reportForm, treatment_given: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Witnesses</label>
              <Input
                placeholder="Names of any witnesses"
                value={reportForm.witnesses}
                onChange={(e) => setReportForm({ ...reportForm, witnesses: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <input
                type="checkbox"
                id="requires_medical"
                checked={reportForm.requires_medical_attention}
                onChange={(e) => setReportForm({ ...reportForm, requires_medical_attention: e.target.checked })}
                className="w-4 h-4 accent-red-500"
              />
              <label htmlFor="requires_medical" className="text-sm text-white cursor-pointer">
                Requires further medical attention
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleCreateReport} className="bg-red-500 hover:bg-red-600 text-white">
              <Send className="w-4 h-4 mr-2" />
              Create & Notify Parent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Report Modal */}
      <Dialog open={!!viewReport} onOpenChange={() => setViewReport(null)}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          {viewReport && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-500" />
                  Accident Report
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">{viewReport.student_name}</h3>
                  {getStatusBadge(viewReport)}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white/50">Date:</span>
                    <p className="text-white">{formatDate(viewReport.incident_date || viewReport.date_time)}</p>
                  </div>
                  <div>
                    <span className="text-white/50">Time:</span>
                    <p className="text-white">{viewReport.incident_time}</p>
                  </div>
                </div>

                {viewReport.location && (
                  <div>
                    <span className="text-white/50 text-sm">Location:</span>
                    <p className="text-white">{viewReport.location}</p>
                  </div>
                )}

                <div>
                  <span className="text-white/50 text-sm">Description:</span>
                  <p className="text-white whitespace-pre-wrap">{viewReport.description}</p>
                </div>

                {viewReport.injuries && (
                  <div>
                    <span className="text-white/50 text-sm">Injuries:</span>
                    <p className="text-white">{viewReport.injuries}</p>
                  </div>
                )}

                {viewReport.treatment_given && (
                  <div>
                    <span className="text-white/50 text-sm">Treatment Given:</span>
                    <p className="text-white">{viewReport.treatment_given}</p>
                  </div>
                )}

                {viewReport.witnesses && (
                  <div>
                    <span className="text-white/50 text-sm">Witnesses:</span>
                    <p className="text-white">{viewReport.witnesses}</p>
                  </div>
                )}

                <div className="text-sm text-white/50 pt-2 border-t border-white/10">
                  Reported by {viewReport.created_by_name} on {formatDate(viewReport.created_at)}
                </div>

                {viewReport.signed_at && (
                  <Card className="bg-green-500/10 border-green-500/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="font-semibold text-white">Signed by Parent</span>
                      </div>
                      <p className="text-white/70 text-sm">
                        {viewReport.signed_by_name} ({viewReport.signer_relationship}) - {formatDate(viewReport.signed_at)}
                      </p>
                      {viewReport.signature_notes && (
                        <p className="text-white/60 text-sm mt-2">Note: {viewReport.signature_notes}</p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewReport(null)} className="border-white/10 text-white hover:bg-white/10">
                  Close
                </Button>
                {!viewReport.signed_at && (canCreate || user?.role === 'parent') && (
                  <Button 
                    onClick={() => setSignatureModalOpen(true)} 
                    className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
                  >
                    <Pen className="w-4 h-4 mr-2" />
                    Sign Report
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={signatureModalOpen} onOpenChange={setSignatureModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pen className="w-5 h-5 text-[#FF007F]" />
              Sign Accident Report
            </DialogTitle>
            <DialogDescription className="text-white/60">
              By signing, you acknowledge receipt of this accident report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-white/70 mb-1 block">Your Full Name *</label>
              <Input
                placeholder="Enter your full name"
                value={signatureForm.signer_name}
                onChange={(e) => setSignatureForm({ ...signatureForm, signer_name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Relationship to Student</label>
              <Select 
                value={signatureForm.relationship} 
                onValueChange={(v) => setSignatureForm({ ...signatureForm, relationship: v })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-white/10">
                  <SelectItem value="parent" className="text-white">Parent</SelectItem>
                  <SelectItem value="guardian" className="text-white">Guardian</SelectItem>
                  <SelectItem value="carer" className="text-white">Carer</SelectItem>
                  <SelectItem value="other" className="text-white">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Additional Notes (optional)</label>
              <Textarea
                placeholder="Any comments or concerns..."
                value={signatureForm.notes}
                onChange={(e) => setSignatureForm({ ...signatureForm, notes: e.target.value })}
                className="bg-white/5 border-white/10 text-white resize-none"
                rows={2}
              />
            </div>
            <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white/60">
              <p>By clicking "Sign & Confirm" you confirm that:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>You have read and understood this accident report</li>
                <li>You acknowledge the incident and treatment described</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignatureModalOpen(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleSignReport} className="bg-[#FF007F] hover:bg-[#D6006A] text-white">
              <CheckCircle className="w-4 h-4 mr-2" />
              Sign & Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccidentReports;
